import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { discountsApi } from '../api/discounts';
import { publicApi } from '../api/public';
import DiscountCard from '../components/discounts/DiscountCard';
import DiscountFilters from '../components/discounts/DiscountFilters';
import { AlertCircle } from 'lucide-react';

const DiscountCatalog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  
  // Получаем параметры из URL
  const initialFilters = {
    searchQuery: queryParams.get('search') || '',
    categoryId: queryParams.get('categoryId') || '',
    partnerId: queryParams.get('partnerId') || '',
    type: queryParams.get('type') || '',
    sortBy: queryParams.get('sortBy') || 'newest'
  };
  
  const [filters, setFilters] = useState(initialFilters);
  
  // Запрос категорий
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => publicApi.getCategories().then(res => res.data),
    staleTime: 24 * 60 * 60 * 1000, // 24 часа
  });
  
  // Запрос скидок с фильтрами
  const { data: discountsData, isLoading, isError } = useQuery({
    queryKey: ['discounts', filters],
    queryFn: async () => {
      // Базовый запрос
      let response;
      
      if (filters.searchQuery) {
        response = await discountsApi.searchDiscounts(filters.searchQuery);
      } else if (filters.categoryId) {
        response = await discountsApi.getDiscountsByCategory(filters.categoryId);
      } else if (filters.partnerId) {
        response = await discountsApi.getDiscountsByPartner(filters.partnerId);
      } else if (filters.type) {
        response = await discountsApi.getDiscountsByType(filters.type);
      } else {
        response = await discountsApi.getAllActiveDiscounts();
      }
      
      let discounts = response.data;
      
      // Сортировка на клиенте
      if (filters.sortBy === 'newest') {
        discounts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } else if (filters.sortBy === 'expiringSoon') {
        discounts.sort((a, b) => {
          if (!a.endDate) return 1;
          if (!b.endDate) return -1;
          return new Date(a.endDate) - new Date(b.endDate);
        });
      } else if (filters.sortBy === 'popular') {
        discounts.sort((a, b) => b.usageCount - a.usageCount);
      } else if (filters.sortBy === 'percentageHighest') {
        discounts.sort((a, b) => {
          if (a.type !== 'PERCENTAGE') return 1;
          if (b.type !== 'PERCENTAGE') return -1;
          return b.percentageValue - a.percentageValue;
        });
      } else if (filters.sortBy === 'valueHighest') {
        discounts.sort((a, b) => {
          if (a.type !== 'FIXED') return 1;
          if (b.type !== 'FIXED') return -1;
          return b.fixedValue - a.fixedValue;
        });
      }
      
      return discounts;
    },
    staleTime: 2 * 60 * 1000, // 2 минуты
  });
  
  // Обновление URL при изменении фильтров
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters.searchQuery) params.set('search', filters.searchQuery);
    if (filters.categoryId) params.set('categoryId', filters.categoryId);
    if (filters.partnerId) params.set('partnerId', filters.partnerId);
    if (filters.type) params.set('type', filters.type);
    if (filters.sortBy && filters.sortBy !== 'newest') params.set('sortBy', filters.sortBy);
    
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);
  
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2 md:mb-0">Каталог знижок</h1>
        
      </div>
      
      <DiscountFilters
        categories={categoriesData || []}
        initialFilters={filters}
        onFilterChange={handleFilterChange}
      />
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 h-48 rounded-t-lg"></div>
              <div className="bg-white p-4 rounded-b-lg">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-medium text-red-800">Помилка завантаження</h3>
            <p className="text-red-700">Не вдалося завантажити знижки. Спробуйте оновити сторінку.</p>
          </div>
        </div>
      ) : discountsData?.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="text-amber-500 mt-0.5 mr-2 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-medium text-amber-800">Нічого не знайдено</h3>
            <p className="text-amber-700">За вашим запитом не знайдено жодної знижки. Спробуйте змінити критерії пошуку.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {discountsData.map(discount => (
            <DiscountCard key={discount.id} discount={discount} />
          ))}
        </div>
      )}
    </div>
  );
};


export default DiscountCatalog;